import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, MoveRight, MoveLeft } from "lucide-react";

/**
 * SpotlightTour — a reusable guided tour engine.
 *
 * Each step describes:
 *  - target: a CSS selector (usually `[data-testid="…"]`). If null → centered.
 *  - route: optional route to navigate to before showing the step.
 *  - title / body / icon: what to render in the tooltip.
 *  - eyebrow: optional label above title (e.g. "Agent · Step 3 of 5").
 *
 * The engine renders:
 *  - A 4-piece dark overlay that leaves the target rectangle "cut out"
 *    (a spotlight). If the target is missing/off-screen it degrades to a
 *    full-screen dim with a centered tooltip so no step ever hard-blocks.
 *  - A tooltip card positioned above/below the target depending on space,
 *    with iKEY-branded primary-color CTA + a Skip link.
 *
 * The parent supplies `onFinish` and `onSkip` callbacks so the caller can
 * mark server flags however it wants. Local state is kept minimal (step
 * index + measured rect) so re-renders stay cheap.
 */
export default function SpotlightTour({
  steps,
  onFinish,
  onSkip,
  brandName = "iKEY",
  testIdPrefix = "tour",
}) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const s = steps[step];
  const isLast = step === steps.length - 1;

  // If this step wants a specific route, hop there first. React Router's
  // effect runs on next tick so we suspend "ready" until location matches.
  useEffect(() => {
    if (!s) return;
    if (s.route && location.pathname !== s.route) {
      setReady(false);
      nav(s.route);
    } else {
      setReady(true);
    }
  }, [step, s, location.pathname, nav]);

  // Measure the target element once the route matches. Poll for up to 2s
  // because some pages load content asynchronously (Settings pulls agent,
  // Documents pulls list). If the element never appears we fall back to
  // centered mode by leaving rect=null.
  const measure = useCallback(() => {
    if (!s || !ready) return;
    if (!s.target) { setRect(null); return; }
    const el = document.querySelector(s.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top + window.scrollY,
      left: r.left + window.scrollX,
      width: r.width,
      height: r.height,
      viewportTop: r.top,
      viewportLeft: r.left,
    });
    // Ensure the target is visible.
    if (r.top < 80 || r.bottom > window.innerHeight - 40) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [s, ready]);

  useLayoutEffect(() => {
    if (!ready) return;
    // Try immediately, then a couple of retries for late-mounting targets.
    measure();
    const timers = [50, 200, 500, 900, 1500].map(t => setTimeout(measure, t));
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [ready, measure]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onSkip?.();
      if (e.key === "ArrowRight" && !isLast) setStep(step + 1);
      if (e.key === "ArrowLeft" && step > 0) setStep(step - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, isLast, onSkip]);

  if (!s) return null;

  // Compute tooltip position:
  // - If we have a rect and there's room BELOW the target, put tooltip below.
  // - Else if room ABOVE, put it above.
  // - Otherwise (or no rect) center it.
  const TOOLTIP_W = 380;
  const GAP = 16;
  let tooltip = null;
  const padding = 8;
  if (rect) {
    const spaceBelow = window.innerHeight - (rect.viewportTop + rect.height);
    const spaceAbove = rect.viewportTop;
    const placeBelow = spaceBelow > 220 || spaceBelow > spaceAbove;
    const top = placeBelow
      ? rect.top + rect.height + GAP
      : Math.max(16 + window.scrollY, rect.top - GAP - 220);
    // Horizontal center on the target, clamped to viewport.
    const idealLeft = rect.left + rect.width / 2 - TOOLTIP_W / 2;
    const maxLeft = window.scrollX + window.innerWidth - TOOLTIP_W - 16;
    const left = Math.max(16 + window.scrollX, Math.min(idealLeft, maxLeft));
    tooltip = { top, left, width: TOOLTIP_W, placeBelow };
  }

  // Spotlight rectangle (with a bit of breathing room around the target).
  const spot = rect ? {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  } : null;

  const Icon = s.icon;

  return (
    <div
      className="fixed inset-0 z-[80]"
      role="dialog"
      aria-label={`${brandName} tour`}
      data-testid={testIdPrefix}
    >
      {/* Dim overlay. If we have a spot, use SVG mask so the click-through
          zone lines up perfectly. Otherwise a plain dimmer. */}
      {spot ? (
        <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={onSkip} aria-hidden="true">
          <defs>
            <mask id="spot-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={spot.left - window.scrollX}
                y={spot.top - window.scrollY}
                width={spot.width}
                height={spot.height}
                rx="10"
                fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(15,23,42,0.72)" mask="url(#spot-mask)" />
          <rect
            x={spot.left - window.scrollX}
            y={spot.top - window.scrollY}
            width={spot.width}
            height={spot.height}
            rx="10"
            fill="none"
            stroke="var(--primary-hex, #1F6FEB)"
            strokeWidth="2"
            className="pointer-events-none"
          />
        </svg>
      ) : (
        <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onSkip} aria-hidden="true" />
      )}

      {/* Tooltip card */}
      <div
        className="absolute rounded-xl bg-white shadow-2xl overflow-hidden"
        style={tooltip
          ? { top: tooltip.top, left: tooltip.left, width: tooltip.width }
          : { top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: TOOLTIP_W }
        }
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testIdPrefix}-card`}
      >
        <button
          type="button"
          onClick={onSkip}
          className="absolute top-3 right-3 p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          aria-label="Skip tour"
          data-testid={`${testIdPrefix}-close`}
        >
          <X className="h-4 w-4" />
        </button>
        <div className="p-5 pr-10">
          {s.eyebrow && (
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{s.eyebrow}</div>
          )}
          <div className="mt-1 flex items-start gap-3">
            {Icon && (
              <div className="h-9 w-9 shrink-0 rounded-md flex items-center justify-center text-white" style={{ background: "var(--primary-hex, #1F6FEB)" }}>
                <Icon className="h-4 w-4" />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900" data-testid={`${testIdPrefix}-title`}>{s.title}</h3>
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-700 leading-relaxed" data-testid={`${testIdPrefix}-body`}>{s.body}</p>
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 bg-slate-50">
          <div className="flex items-center gap-1" aria-hidden="true">
            {steps.map((sp, i) => (
              <span
                key={sp.title || `step-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === step ? "w-5" : "w-1.5 bg-slate-300"}`}
                style={i === step ? { background: "var(--primary-hex, #1F6FEB)" } : undefined}
              />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onSkip}
              className="text-xs font-medium text-slate-500 hover:text-slate-800 px-2 py-1"
              data-testid={`${testIdPrefix}-skip`}
            >
              Skip
            </button>
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                data-testid={`${testIdPrefix}-back`}
              >
                <MoveLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
            <button
              type="button"
              onClick={() => isLast ? onFinish?.() : setStep(step + 1)}
              className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
              style={{ background: "var(--primary-hex, #1F6FEB)" }}
              data-testid={`${testIdPrefix}-next`}
            >
              {isLast ? "Done" : "Next"} <MoveRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
